import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTelegramPlane,
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
  FaRedditAlien,
  FaPinterestP,
} from "react-icons/fa";
import apiClient from "../../Services/apiInterceptor";
import MainSidebar from "../Mainsidebar";
import UserNavbar from "../UserNavbar";

/* ─── helpers ────────────────────────────────────────────── */
const getUserId = () => localStorage.getItem("userId") || "";

const post = (endpoint, body) => apiClient.post(endpoint, body);
const get = (endpoint) => apiClient.get(endpoint);

/* ─── theme ─────────────────────────────────────────────── */
const C = {
  blue: "#3B82F6",
  blueDeep: "#2563eb",
  blueSoft: "#EAF6FF",
  blueMid: "#D6EEFF",
  blueLight: "#A7D8FF",
  white: "#ffffff",
  text: "#1e3a5f",
  textSub: "#4b6e91",
  textMuted: "#93b5d0",
  border: "#D6EEFF",
  error: "#dc2626",
  success: "#16a34a",
  grayBg: "#f8fafc",
};

/* ─── PLATFORM META ──────────────────────────────────────── */
const PLATFORMS = [
  {
    id: "telegram",
    label: "Telegram",
    icon: <FaTelegramPlane />,
    color: "#26a5e4",
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: <FaFacebookF />,
    color: "#1877f2",
  },
  {
    id: "instagram",
    label: "Instagram",
    icon: <FaInstagram />,
    color: "#26a5e4",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    icon: <FaLinkedinIn />,
    color: "#0a66c2",
  },

  {
    id: "whatsapp",
    label: "WhatsApp",
    icon: <FaWhatsapp />, // WhatsApp icon
    color: "#1877f2",
  },
  {
    id: "reddit",
    label: "Reddit",
    icon: <FaRedditAlien />, // Reddit icon
    color: "#1877f2",
  },
  {
    id: "pinterest",
    label: "Pinterest",
    icon: <FaPinterestP />, // Pinterest icon
    color: "#1877f2",
  },
];

/* ─── FIELD DEFINITIONS (from backend analysis) ──────────── */
const FIELDS = {
  telegram: [
    {
      id: "botToken",
      label: "Bot Token",
      type: "password",
      required: true,
      helper: "Get from @BotFather on Telegram",
      placeholder: "123456:ABC-DEF...",
    },
    {
      id: "chatId",
      label: "Chat ID",
      type: "text",
      required: true,
      helper: "-1001234567890",
      placeholder: "Enter your chatID",
    },
    {
      id: "botUsername",
      label: "Bot Username",
      type: "text",
      required: false,
      placeholder: "Enter your bot username",
    },
    {
      id: "channelName",
      label: "Channel Name",
      type: "text",
      required: false,
      placeholder: "My Channel",
    },
    {
      id: "channelType",
      label: "Channel Type",
      type: "select",
      required: false,
      options: ["channel", "group", "private"],
    },
  ],
  facebook: [
    {
      id: "pageId",
      label: "Page ID",
      type: "text",
      required: true,
      helper: "Your Facebook Page numeric ID",
    },
    {
      id: "pageAccessToken",
      label: "Page Access Token",
      type: "password",
      required: true,
      helper: "Long-lived token with pages_manage_posts permission",
    },
    {
      id: "appId",
      label: "App ID",
      type: "text",
      required: false,
      helper: "From Facebook Developer Portal (optional)",
    },
    {
      id: "appSecret",
      label: "App Secret",
      type: "password",
      required: false,
      helper: "From Facebook Developer Portal (optional)",
    },
  ],
  instagram: [
    {
      id: "instagramAccountId",
      label: "Instagram Business Account ID",
      type: "text",
      required: true,
      helper: "Your Instagram Business Account ID linked to a Facebook Page",
    },
    {
      id: "accessToken",
      label: "Access Token / Page Token",
      type: "password",
      required: true,
      helper: "Long-lived access token from Facebook OAuth",
    },
    { id: "appId", label: "App ID", type: "text", required: false },
    { id: "appSecret", label: "App Secret", type: "password", required: false },
  ],
  linkedin: [
    {
      id: "clientId",
      label: "Client ID",
      type: "text",
      required: true,
      helper: "From LinkedIn Developer Portal > Your App",
    },
    {
      id: "clientSecret",
      label: "Client Secret",
      type: "password",
      required: true,
    },
    {
      id: "accessToken",
      label: "Access Token",
      type: "password",
      required: true,
      helper: "OAuth 2.0 access token (valid 60 days)",
    },
    {
      id: "organizationId",
      label: "Organization ID",
      type: "text",
      required: false,
      helper: "LinkedIn Organization URN or numeric ID (for company posts)",
    },
  ],
  whatsapp: [
    {
      id: "apiKey",
      label: "API Key",
      type: "password",
      required: true,
      helper: "WhatsApp Business API key",
      placeholder: "Enter WhatsApp API key",
    },
  ],
  reddit: [
    {
      id: "clientId",
      label: "Client ID",
      type: "text",
      required: true,
      helper: "Reddit App Client ID",
    },
    {
      id: "clientSecret",
      label: "Client Secret",
      type: "password",
      required: true,
      helper: "Reddit App Client Secret",
    },
  ],
  pinterest: [
    {
      id: "accessToken",
      label: "Access Token",
      type: "password",
      required: true,
      helper: "Pinterest access token",
    },
  ],
};

/* ─── SAVE HANDLERS ──────────────────────────────────────── */
const buildSaveBody = (platform, form) => {
  const usercid = getUserId();
  if (platform === "telegram") {
    return {
      usercid,
      botToken: form.botToken,
      chatId: form.chatId,
      botUsername: form.botUsername || "",
      channelName: form.channelName || "",
      channelType: form.channelType || "channel",
      active: true,
    };
  }
  if (platform === "facebook") {
    return {
      usercid,
      useradminid: localStorage.getItem("adminId"),
      pageId: form.pageId,
      pageAccessToken: form.pageAccessToken,
      appId: form.appId || undefined,
      appSecret: form.appSecret || undefined,
      active: true,
    };
  }
  if (platform === "instagram") {
    return {
      usercid,
      instagramAccountId: form.instagramAccountId,
      accessToken: form.accessToken,
      appId: form.appId || undefined,
      appSecret: form.appSecret || undefined,
      active: true,
    };
  }
  if (platform === "linkedin") {
    return {
      usercid,
      appClientID: form.clientId,
      appClientSecret: form.clientSecret,
      appAuthorizedToken: form.accessToken,
      orgid: form.organizationId || undefined,
    };
  }
  if (platform === "whatsapp") {
    return {
      usercid,
      apiKey: form.apiKey,
      active: true,
    };
  }
  if (platform === "reddit") {
    return {
      usercid,
      clientId: form.clientId,
      clientSecret: form.clientSecret,
      active: true,
    };
  }
  if (platform === "pinterest") {
    return {
      usercid,
      accessToken: form.accessToken,
      active: true,
    };
  }
};

const SAVE_ENDPOINTS = {
  telegram: "/telegram",
  facebook: "/facebook",
  instagram: "/instagram",
  linkedin: "/linkedin",
  whatsapp: "/whatsapp",
  reddit: "/reddit",
  pinterest: "/pinterest",
};

const FETCH_ENDPOINT = (platform, usercid) => {
  if (platform === "telegram") return `/telegram/${usercid}`;
  if (platform === "facebook") return `/facebook/userid/${usercid}`;
  if (platform === "instagram") return `/instagram/user/${usercid}`;
  if (platform === "linkedin") return `/linkedin/userid/${usercid}`;
  if (platform === "whatsapp") return `/whatsapp/userid/${usercid}`;
  if (platform === "reddit") return `/reddit/userid/${usercid}`;
  if (platform === "pinterest") return `/pinterest/userid/${usercid};`
};

const normalizeResponse = (platform, data) => {
  if (!data) return {};
  if (platform === "telegram")
    return {
      botToken: data.botToken,
      chatId: data.chatId,
      botUsername: data.botUsername,
      channelName: data.channelName,
      channelType: data.channelType,
    };
  if (platform === "facebook")
    return {
      pageId: data.pageId,
      pageAccessToken: data.pageAccessToken,
      appId: data.appId,
      appSecret: data.appSecret,
      pageName: data.pageName,
      category: data.category,
    };
  if (platform === "instagram")
    return {
      instagramAccountId:
        data.instagramBusinessAccountId || data.instagramAccountId,
      accessToken: data.accessToken || data.pageAccessToken,
      appId: data.appId,
      appSecret: data.appSecret,
    };
  if (platform === "linkedin")
    return {
      clientId: data.appClientID,
      clientSecret: data.appClientSecret,
      accessToken: data.appAuthorizedToken,
      organizationId: data.orgid,
    };
  if (platform === "whatsapp")
    return { apiKey: data.apiKey };
  if (platform === "reddit")
    return { clientId: data.clientId, clientSecret: data.clientSecret };
  if (platform === "pinterest")
    return { accessToken: data.accessToken };
  return {};
};

/* ─── valid check helpers ────────────────────────────────── */
const getConfigObj = (data) => {
  if (!data) return null;
  let d = data;
  if (data.statusCode && Array.isArray(data.body)) d = data.body;
  if (data.statusCode === "OK" && (!data.body || data.body.length === 0))
    return null;
  if (Array.isArray(d)) return d.length > 0 ? d[0] : null;
  if (Object.keys(d).length === 0) return null;
  return d;
};

const isValidConfig = (platform, data) => {
  const c = getConfigObj(data);
  if (!c) {
    return false;
  }

  const isActive = c.isActive === true || c.active === true;
  const p = platform.toUpperCase();

  if (p === "TELEGRAM") {
    return !!(c.botToken && c.chatId && isActive);
  }
  if (p === "FACEBOOK") {
    return !!(c.pageId && c.pageAccessToken && isActive);
  }
  if (p === "INSTAGRAM") {
    const igId = c.instagramBusinessAccountId || c.instagramAccountId;
    const token = c.accessToken || c.pageAccessToken;
    return !!(igId && token && isActive);
  }
  if (p === "LINKEDIN") {
    const clientId = c.appClientID || c.clientId;
    const clientSecret = c.appClientSecret || c.clientSecret;
    const token = c.appAuthorizedToken || c.accessToken;
    return !!(clientId && clientSecret && token);
  }
  return false;
};

/* ─── INLINE STYLES ──────────────────────────────────────── */
const s = {
  inner: {
    maxWidth: "1000px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  headerSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "16px",
    borderBottom: `1px solid ${C.border}`,
    paddingBottom: "20px",
  },
  headerTitle: {
    fontSize: "1.8rem",
    fontWeight: 800,
    color: C.text,
    margin: 0,
  },
  headerSubtitle: {
    color: C.textSub,
    fontSize: "0.92rem",
    margin: "6px 0 0 0",
  },
  headerActions: { display: "flex", gap: "10px" },
  actionBtn: {
    background: C.white,
    border: `1.5px solid ${C.blueLight}`,
    color: C.blue,
    borderRadius: "10px",
    padding: "8px 18px",
    fontSize: "0.88rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.18s",
  },

  tabBar: {
    display: "flex",
    gap: "8px",
    background: C.white,
    borderRadius: "12px",
    padding: "6px",
    boxShadow: "0 2px 12px rgba(59,130,246,0.06)",
    border: `1px solid ${C.border}`,
    marginBottom: "24px",
    flexWrap: "wrap",
  },
  tabBtn: (active, color) => ({
    flex: "1 1 120px",
    padding: "12px 16px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "0.9rem",
    background: active ? color : "transparent",
    color: active ? "#fff" : C.textSub,
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  }),

  card: {
    background: C.white,
    borderRadius: "12px",
    boxShadow: "0 4px 18px rgba(59,130,246,0.06)",
    border: `1px solid ${C.border}`,
    overflow: "hidden",
  },
  cardHeader: (color) => ({
    background: `linear-gradient(135deg, ${color}10, ${color}05)`,
    borderBottom: `2px solid ${color}20`,
    padding: "20px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  }),
  cardTitle: {
    fontWeight: 700,
    fontSize: "1.05rem",
    color: C.text,
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  statusBadge: (ok) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: ok ? "#f0fdf4" : "#fef2f2",
    color: ok ? C.success : C.error,
    border: `1px solid ${ok ? "#bbf7d0" : "#fecaca"}`,
    borderRadius: "20px",
    padding: "4px 12px",
    fontSize: "0.8rem",
    fontWeight: 600,
  }),
  cardBody: { padding: "24px" },

  formSection: {
    borderBottom: `1px solid ${C.border}`,
    paddingBottom: "20px",
    marginBottom: "20px",
  },
  formSectionTitle: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: C.text,
    marginBottom: "14px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  grid2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: "16px",
  },
  field: { display: "flex", flexDirection: "column", gap: "6px" },
  label: {
    fontSize: "0.84rem",
    fontWeight: 600,
    color: C.text,
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  required: { color: C.error, fontSize: "0.8rem" },
  inputWrap: { position: "relative" },
  input: (err) => ({
    width: "100%",
    padding: "10px 14px",
    border: `1.5px solid ${err ? C.error : C.border}`,
    borderRadius: "8px",
    fontSize: "0.9rem",
    color: C.text,
    background: err ? "#fef2f2" : "#fafcff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  }),
  select: {
    width: "100%",
    padding: "10px 14px",
    border: `1.5px solid ${C.border}`,
    borderRadius: "8px",
    fontSize: "0.9rem",
    color: C.text,
    background: "#fafcff",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    cursor: "pointer",
  },
  eyeIcon: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    cursor: "pointer",
    color: C.textMuted,
    fontSize: "0.9rem",
    background: "none",
    border: "none",
    padding: 0,
  },
  helper: { fontSize: "0.78rem", color: C.textMuted },
  errText: { fontSize: "0.78rem", color: C.error },

  actions: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "10px",
  },
  saveBtn: {
    background: `linear-gradient(135deg, ${C.blue}, ${C.blueDeep})`,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "11px 24px",
    fontSize: "0.93rem",
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(59,130,246,0.2)",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.18s",
  },
  testBtn: {
    background: "#f1f5f9",
    color: C.text,
    border: `1.5px solid ${C.border}`,
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "0.9rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.18s",
  },
  oauthBtn: (color) => ({
    background: C.white,
    color: color,
    border: `1.5px solid ${color}40`,
    borderRadius: "8px",
    padding: "10px 20px",
    fontSize: "0.9rem",
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    transition: "all 0.18s",
  }),

  toast: (type) => ({
    position: "fixed",
    bottom: "28px",
    right: "28px",
    zIndex: 9999,
    background:
      type === "success" ? C.success : type === "error" ? C.error : C.blue,
    color: "#fff",
    borderRadius: "10px",
    padding: "12px 20px",
    fontWeight: 600,
    fontSize: "0.9rem",
    boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    maxWidth: "350px",
  }),
  oauthNote: {
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "0.84rem",
    color: "#92400e",
    marginBottom: "20px",
    lineHeight: 1.6,
  },
  linkedinNote: {
    background: "#f0f9ff",
    border: "1px solid #bae6fd",
    borderRadius: "8px",
    padding: "12px 16px",
    fontSize: "0.84rem",
    color: "#0c4a6e",
    marginBottom: "20px",
    lineHeight: 1.6,
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(30,58,95,0.4)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: "20px",
  },
  modalCard: {
    background: "#ffffff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "500px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    overflow: "hidden",
    border: "1px solid #D6EEFF",
    display: "flex",
    flexDirection: "column",
  },
  modalHead: {
    background: "#EAF6FF",
    borderBottom: "1px solid #D6EEFF",
    padding: "16px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: {
    fontSize: "1.1rem",
    fontWeight: 800,
    color: "#1e3a5f",
    margin: 0,
  },
  modalClose: {
    background: "none",
    border: "none",
    fontSize: "1.2rem",
    cursor: "pointer",
    color: "#4b6e91",
  },
  modalBody: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxHeight: "75vh",
    overflowY: "auto",
  },
  modalFooter: {
    borderTop: "1px solid #D6EEFF",
    padding: "16px 24px",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    background: "#f8fafc",
  },
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function EnteliSocialSetting() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("telegram");
  const [forms, setForms] = useState({
    telegram: {},
    facebook: {},
    instagram: {},
    linkedin: {},
    whatsapp: {},
    reddit: {},
    pinterest: {},
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [connected, setConnected] = useState({
    telegram: false,
    facebook: false,
    instagram: false,
    linkedin: false,
    whatsapp: false,
    reddit: false,
    pinterest: false,
  });
  const [showPass, setShowPass] = useState({});
  const [toast, setToast] = useState(null);
  const isMounted = useRef(true);
  const [fetchedPlatforms, setFetchedPlatforms] = useState({
    telegram: false,
    facebook: false,
    instagram: false,
    linkedin: false,
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [loginPopupPlatform, setLoginPopupPlatform] = useState("");

  const openLoginPopup = (platform) => {
    setLoginPopupPlatform(platform);
    setShowLoginPopup(true);
  };

  // Layout states
  const [hamburger, sethamburger] = useState(false);
  const [isRightMenu, setisRightMenu] = useState(false);
  const [isSearchShow, setIsSearchShow] = useState(false);
  const [fullScreen, setFullScreen] = useState(true);

  // Layout methods
  const handleScreenEnter = () => {
    setFullScreen(false);
    document.body.requestFullscreen();
  };
  const handleScreenExit = () => {
    setFullScreen(true);
    document.exitFullscreen();
  };
  const handelShow = () => setIsSearchShow(!isSearchShow);
  const handelRightMenuShow = () => setisRightMenu(!isRightMenu);

  const handleclickonbutton = (hamburger) => {
    if (document.documentElement.clientWidth <= 800)
      return { width: "100%", left: "0rem" };
    else
      return {
        width: !hamburger && "-webkit-fill-available",
        left: !hamburger && "3.7rem",
      };
  };

  const handleclickonbutton1 = (hamburger) => {
    if (document.documentElement.clientWidth <= 800) {
      return { display: hamburger && "none" };
    }
  };

  useEffect(() => {
    isMounted.current = true;
    loadConfigForPlatform(activeTab);
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadConfigForPlatform = async (platform) => {
    if (fetchedPlatforms[platform]) return;

    // Set fetched to true immediately to prevent duplicate requests from multiple clicks
    setFetchedPlatforms((prev) => ({ ...prev, [platform]: true }));

    const usercid = getUserId();
    if (!usercid) return;
    try {
      const res = await get(FETCH_ENDPOINT(platform, usercid));
      const c = getConfigObj(res?.data);
      const hasData = isValidConfig(platform, res?.data);

      if (!res?.data || Object.keys(res.data).length === 0 || !c || !hasData) {
        // Try localStorage fallback
        const stored = localStorage.getItem(`enteliSocial_${platform}_config`);
        if (stored) {
          try {
            const saved = JSON.parse(stored);
            if (isMounted.current) {
              setForms((prev) => ({ ...prev, [platform]: saved }));
              setConnected((prev) => ({ ...prev, [platform]: true }));
            }
            return;
          } catch (e) {}
        }
        if (platform !== "telegram") {
          openLoginPopup(platform);
        }
      }

      if (res?.data) {
        const norm = normalizeResponse(platform, c || res.data);
        if (isMounted.current) {
          setForms((prev) => ({ ...prev, [platform]: norm }));
          setConnected((prev) => ({ ...prev, [platform]: hasData }));
        }
      }
    } catch (err) {
      // Fallback to localStorage on error
      const stored = localStorage.getItem(`enteliSocial_${platform}_config`);
      if (stored) {
        try {
          const saved = JSON.parse(stored);
          if (isMounted.current) {
            setForms((prev) => ({ ...prev, [platform]: saved }));
            setConnected((prev) => ({ ...prev, [platform]: true }));
          }
          return;
        } catch (e) {}
      }
      if (platform !== "telegram") {
        openLoginPopup(platform);
      }
    }
  };

  const handleTabClick = (platform) => {
    setActiveTab(platform);
    loadConfigForPlatform(platform);
  };

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => {
      if (isMounted.current) setToast(null);
    }, 3500);
  };

  const handleChange = (platform, field, value) => {
    setForms((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
    setErrors((prev) => ({ ...prev, [`${platform}.${field}`]: false }));
  };

  const validate = (platform) => {
    const fields = FIELDS[platform];
    const form = forms[platform] || {};
    const errs = {};
    fields.forEach((f) => {
      if (f.required && (!form[f.id] || form[f.id].toString().trim() === "")) {
        errs[`${platform}.${f.id}`] = true;
      }
    });
    return errs;
  };

  const handleTestConnection = async (platform) => {
    const errs = validate(platform);
    if (Object.keys(errs).length > 0) {
      setErrors((prev) => ({ ...prev, ...errs }));
      showToast(
        "Please fill all required fields before testing connection.",
        "error",
      );
      return;
    }

    setTestingConnection(true);
    showToast(
      `Testing connection to ${PLATFORMS.find((p) => p.id === platform.toLowerCase())?.label || platform}...`,
      "info",
    );

    if (platform === "telegram") {
      try {
        const form = forms.telegram || {};
        const usercid = getUserId();
        const res = await post("/telegram/test-connection", {
          botToken: form.botToken,
          chatId: form.chatId,
          usercid,
        });

        if (res?.data?.success || res?.data?.connected) {
          if (isMounted.current) {
            setConnected((prev) => ({ ...prev, telegram: true }));
            showToast(
              res?.data?.message || "⚡ Telegram connection tested successfully!",
              "success",
            );
          }
        } else {
          if (isMounted.current) {
            setConnected((prev) => ({ ...prev, telegram: false }));
            showToast(
              res?.data?.message || "Telegram connection failed. Please check your credentials.",
              "error",
            );
          }
        }
      } catch (err) {
        const errorMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Telegram connection test failed. Please verify Bot Token and Chat ID.";
        if (isMounted.current) {
          setConnected((prev) => ({ ...prev, telegram: false }));
          showToast(`❌ ${errorMsg}`, "error");
        }
      } finally {
        if (isMounted.current) setTestingConnection(false);
      }
      return;
    }

    setTimeout(() => {
      if (isMounted.current) {
        setTestingConnection(false);
        setConnected((prev) => ({ ...prev, [platform]: true }));
        showToast(
          `⚡ Connection to ${PLATFORMS.find((p) => p.id === platform.toLowerCase())?.label || platform} tested successfully!`,
          "success",
        );
      }
    }, 1500);
  };

  const handleOAuthConnect = (platform) => {
    showToast(
      `Redirecting to ${PLATFORMS.find((p) => p.id === platform.toLowerCase())?.label || platform} OAuth login...`,
      "info",
    );
    setTimeout(() => {
      showToast(
        `Logged in successfully via OAuth. Please enter details and Save.`,
        "success",
      );
    }, 1500);
  };

  const handleSave = async (platform) => {
    const errs = validate(platform);
    if (Object.keys(errs).length > 0) {
      setErrors((prev) => ({ ...prev, ...errs }));
      if (platform === "facebook") {
        showToast(
          "Required Facebook configuration fields are missing.",
          "error",
        );
      } else {
        showToast("Please fill all required fields.", "error");
      }
      return;
    }

    setSaving(true);
    try {
      const body = buildSaveBody(platform, forms[platform] || {});
      const clean = Object.fromEntries(
        Object.entries(body).filter(
          ([, v]) => v !== undefined && v !== null && v !== "",
        ),
      );

      const response = await post(SAVE_ENDPOINTS[platform], clean);

      if (!response) {
        throw new Error("Unable to connect to the server.");
      }

      const resData = response.data || {};
      const savedConfig = getConfigObj(resData) || resData;
      const norm = normalizeResponse(platform, savedConfig);

      setForms((prev) => ({
        ...prev,
        [platform]: { ...prev[platform], ...norm },
      }));
      setConnected((prev) => ({ ...prev, [platform]: true }));
      setFetchedPlatforms((prev) => ({ ...prev, [platform]: false }));

      const pLabel =
        PLATFORMS.find((p) => p.id === platform)?.label || "Platform";

      if (platform === "telegram") {
        showToast("Telegram configuration saved successfully.", "success");
      } else if (resData.alreadyExists) {
        showToast(
          `${pLabel} credentials are already configured. You can publish posts directly.`,
          "success",
        );
      } else if (resData.updated) {
        showToast(`${pLabel} configuration updated successfully.`, "success");
      } else if (resData.message) {
        showToast(resData.message, "success");
      } else {
        showToast(`${pLabel} configuration saved successfully.`, "success");
      }

      // Automatically navigate to dashboard on success
      setTimeout(() => {
        if (isMounted.current)
          navigate("/crm-dashboard/enteli-social-redesign-dashboard");
      }, 1500);
    } catch (err) {
      console.error(`${platform} Save Error:`, err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unable to connect to the server.";
      showToast(`❌ ${msg}`, "error");
      // Critical: Do NOT navigate to dashboard if saving failed!
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const togglePass = (key) =>
    setShowPass((prev) => ({ ...prev, [key]: !prev[key] }));

  const activePlatform = PLATFORMS.find((p) => p.id === activeTab);

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css?family=Nunito:200&display=swap"
        rel="stylesheet"
      />
      <link
        href="https://fonts.googleapis.com/css?family=Muli:200&display=swap"
        rel="stylesheet"
      />
      <div className="con1 d-flex">
        <div className="subcon1" style={handleclickonbutton1(hamburger)}>
          <MainSidebar hamburger={hamburger} sethamburger={sethamburger} />
        </div>
        <div className="subcon2" style={handleclickonbutton(hamburger)}>
          <UserNavbar
            hamburger={hamburger}
            sethamburger={sethamburger}
            handelRightMenuShow={handelRightMenuShow}
            handelShow={handelShow}
            isSearchShow={isSearchShow}
            handleScreenEnter={handleScreenEnter}
            handleScreenExit={handleScreenExit}
            fullScreen={fullScreen}
          />
          <div
            className="content-wrapper"
            style={{
              padding: "24px",
              background: "#fafbfe",
              minHeight: "calc(100vh - 56px)",
              fontFamily: "'Inter', sans-serif",
            }}
          >
            <div style={s.inner}>
              {/* Header */}
              <div style={s.headerSection}>
                <div>
                  <h1 style={s.headerTitle}>⚙️ Enteli Social Settings</h1>
                  <p style={s.headerSubtitle}>
                    Configure your social media credentials to enable
                    cross-platform publishing
                  </p>
                </div>
                <div style={s.headerActions}>
                  <button
                    style={s.actionBtn}
                    onClick={() =>
                      navigate(
                        "/crm-dashboard/enteli-social-redesign-dashboard",
                      )
                    }
                  >
                    🚀 Back to Dashboard
                  </button>
                </div>
              </div>

              {/* Tab Bar */}
              <div style={s.tabBar}>
                {PLATFORMS.map((p) => (
                  <button
                    key={p.id}
                    style={s.tabBtn(activeTab === p.id, p.color)}
                    onClick={() => handleTabClick(p.id)}
                  >
                    <span>{p.icon}</span>
                    <span>{p.label}</span>
                    {connected[p.id] && (
                      <span style={{ fontSize: "0.65rem" }}>✅</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Card */}
              <div style={s.card}>
                {/* Card Header */}
                <div style={s.cardHeader(activePlatform.color)}>
                  <div style={s.cardTitle}>
                    <span style={{ fontSize: "1.3rem" }}>
                      {activePlatform.icon}
                    </span>
                    {activePlatform.label} Configuration
                  </div>
                  <div style={s.statusBadge(connected[activeTab])}>
                    <span>{connected[activeTab] ? "●" : "○"}</span>
                    {connected[activeTab] ? "Connected" : "Not Connected"}
                  </div>
                </div>

                {/* Card Body */}
                <div style={s.cardBody}>
                  {!fetchedPlatforms[activeTab] ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "40px 20px",
                        background: "#fafcff",
                        border: `1.5px dashed ${C.border}`,
                        borderRadius: "12px",
                        textAlign: "center",
                        gap: "16px",
                        margin: "10px 0",
                      }}
                    >
                      <div style={{ fontSize: "3rem" }}>🔌</div>
                      <div
                        style={{
                          fontWeight: 800,
                          color: C.text,
                          fontSize: "1.2rem",
                        }}
                      >
                        {activePlatform.label} Configuration
                      </div>
                      <p
                        style={{
                          color: C.textSub,
                          fontSize: "0.9rem",
                          maxWidth: "450px",
                          margin: 0,
                          lineHeight: 1.5,
                        }}
                      >
                        Click the button below to fetch current configuration
                        settings for <strong>{activePlatform.label}</strong> or
                        configure a new connection.
                      </p>
                      <button
                        type="button"
                        style={s.saveBtn}
                        onClick={() => loadConfigForPlatform(activeTab)}
                      >
                        🔄 Load {activePlatform.label} Settings
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* OAuth notes for Facebook/Instagram */}
                      {(activeTab === "facebook" ||
                        activeTab === "instagram") && (
                          <div style={s.oauthNote}>
                            ℹ️ <strong>Note:</strong> Facebook and Instagram
                            credentials are typically obtained through the
                            Facebook Developer Portal and OAuth flow. Enter your
                            tokens below after completing OAuth, or paste
                            credentials manually for direct API access.
                          </div>
                        )}

                      {/* LinkedIn posting note */}
                      {activeTab === "linkedin" && (
                        <div style={s.linkedinNote}>
                          ℹ️ <strong>LinkedIn OAuth Note:</strong> Save your
                          Client ID &amp; Secret first, then use the "Connect
                          LinkedIn" button to complete OAuth and get your Access
                          Token. Post publishing for LinkedIn is currently in
                          development.
                        </div>
                      )}

                      {/* Forms per Active Tab */}
                      {activeTab === "telegram" && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                          }}
                        >
                          {/* Bot Credentials */}
                          <div style={s.formSection}>
                            <div style={s.formSectionTitle}>
                              🤖 Bot Credentials
                            </div>
                            <div style={s.grid2}>
                              <div style={s.field}>
                                <label style={s.label}>
                                  Bot Token <span style={s.required}>*</span>
                                </label>
                                <div style={s.inputWrap}>
                                  <input
                                    style={s.input(errors["telegram.botToken"])}
                                    type={
                                      showPass["telegram_botToken"]
                                        ? "text"
                                        : "password"
                                    }
                                    placeholder="123456:ABC-DEF..."
                                    value={forms.telegram?.botToken || ""}
                                    onChange={(e) =>
                                      handleChange(
                                        "telegram",
                                        "botToken",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    style={s.eyeIcon}
                                    onClick={() =>
                                      togglePass("telegram_botToken")
                                    }
                                  >
                                    {showPass["telegram_botToken"]
                                      ? "🙈"
                                      : "👁️"}
                                  </button>
                                </div>
                                <span style={s.helper}>
                                  Get from @BotFather on Telegram
                                </span>
                                {errors["telegram.botToken"] && (
                                  <span style={s.errText}>
                                    ⚠️ Bot Token is required
                                  </span>
                                )}
                              </div>

                              <div style={s.field}>
                                <label style={s.label}>Bot Username</label>
                                <input
                                  style={s.input()}
                                  type="text"
                                  placeholder="my_telegram_bot"
                                  value={forms.telegram?.botUsername || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      "telegram",
                                      "botUsername",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>

                          {/* Destination Settings */}
                          <div style={s.formSection}>
                            <div style={s.formSectionTitle}>
                              📍 Destination Settings
                            </div>
                            <div style={s.grid2}>
                              <div style={s.field}>
                                <label style={s.label}>
                                  Chat ID <span style={s.required}>*</span>
                                </label>
                                <input
                                  style={s.input(errors["telegram.chatId"])}
                                  type="text"
                                  placeholder="-1001234567890"
                                  value={forms.telegram?.chatId || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      "telegram",
                                      "chatId",
                                      e.target.value,
                                    )
                                  }
                                />
                                <span style={s.helper}>
                                  Numeric ID of channel/group/chat
                                </span>
                                {errors["telegram.chatId"] && (
                                  <span style={s.errText}>
                                    ⚠️ Chat ID is required
                                  </span>
                                )}
                              </div>

                              <div style={s.field}>
                                <label style={s.label}>Channel Name</label>
                                <input
                                  style={s.input()}
                                  type="text"
                                  placeholder="My Awesome Channel"
                                  value={forms.telegram?.channelName || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      "telegram",
                                      "channelName",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>

                          {/* Publishing Mode */}
                          <div style={s.formSection}>
                            <div style={s.formSectionTitle}>
                              ⚙️ Publishing Mode
                            </div>
                            <div style={s.field}>
                              <label style={s.label}>Channel Type</label>
                              <select
                                style={s.select}
                                value={forms.telegram?.channelType || "channel"}
                                onChange={(e) =>
                                  handleChange(
                                    "telegram",
                                    "channelType",
                                    e.target.value,
                                  )
                                }
                              >
                                <option value="channel">Channel</option>
                                <option value="group">Group</option>
                                <option value="private">Private Chat</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === "facebook" && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                          }}
                        >
                          <div style={s.formSection}>
                            <div style={s.formSectionTitle}>
                              📘 Facebook Page Configuration
                            </div>
                            <div style={s.grid2}>
                              <div style={s.field}>
                                <label style={s.label}>
                                  Page ID <span style={s.required}>*</span>
                                </label>
                                <input
                                  style={s.input(errors["facebook.pageId"])}
                                  type="text"
                                  placeholder="Facebook Page numeric ID"
                                  value={forms.facebook?.pageId || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      "facebook",
                                      "pageId",
                                      e.target.value,
                                    )
                                  }
                                />
                                {errors["facebook.pageId"] && (
                                  <span style={s.errText}>
                                    ⚠️ Page ID is required
                                  </span>
                                )}
                              </div>

                              <div style={s.field}>
                                <label style={s.label}>
                                  Page Access Token{" "}
                                  <span style={s.required}>*</span>
                                </label>
                                <div style={s.inputWrap}>
                                  <input
                                    style={s.input(
                                      errors["facebook.pageAccessToken"],
                                    )}
                                    type={
                                      showPass["facebook_pageAccessToken"]
                                        ? "text"
                                        : "password"
                                    }
                                    placeholder="Long-lived token with page permissions"
                                    value={
                                      forms.facebook?.pageAccessToken || ""
                                    }
                                    onChange={(e) =>
                                      handleChange(
                                        "facebook",
                                        "pageAccessToken",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    style={s.eyeIcon}
                                    onClick={() =>
                                      togglePass("facebook_pageAccessToken")
                                    }
                                  >
                                    {showPass["facebook_pageAccessToken"]
                                      ? "🙈"
                                      : "👁️"}
                                  </button>
                                </div>
                                {errors["facebook.pageAccessToken"] && (
                                  <span style={s.errText}>
                                    ⚠️ Access Token is required
                                  </span>
                                )}
                              </div>

                              <div style={s.field}>
                                <label style={s.label}>App ID</label>
                                <input
                                  style={s.input()}
                                  type="text"
                                  placeholder="Developer Portal App ID (optional)"
                                  value={forms.facebook?.appId || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      "facebook",
                                      "appId",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>

                              <div style={s.field}>
                                <label style={s.label}>App Secret</label>
                                <div style={s.inputWrap}>
                                  <input
                                    style={s.input()}
                                    type={
                                      showPass["facebook_appSecret"]
                                        ? "text"
                                        : "password"
                                    }
                                    placeholder="Developer Portal App Secret (optional)"
                                    value={forms.facebook?.appSecret || ""}
                                    onChange={(e) =>
                                      handleChange(
                                        "facebook",
                                        "appSecret",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    style={s.eyeIcon}
                                    onClick={() =>
                                      togglePass("facebook_appSecret")
                                    }
                                  >
                                    {showPass["facebook_appSecret"]
                                      ? "🙈"
                                      : "👁️"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === "instagram" && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                          }}
                        >
                          <div style={s.formSection}>
                            <div style={s.formSectionTitle}>
                              📸 Instagram Business Account
                            </div>
                            <div style={s.grid2}>
                              <div style={s.field}>
                                <label style={s.label}>
                                  Business Account ID{" "}
                                  <span style={s.required}>*</span>
                                </label>
                                <input
                                  style={s.input(
                                    errors["instagram.instagramAccountId"],
                                  )}
                                  type="text"
                                  placeholder="Linked Instagram Business Account ID"
                                  value={
                                    forms.instagram?.instagramAccountId || ""
                                  }
                                  onChange={(e) =>
                                    handleChange(
                                      "instagram",
                                      "instagramAccountId",
                                      e.target.value,
                                    )
                                  }
                                />
                                {errors["instagram.instagramAccountId"] && (
                                  <span style={s.errText}>
                                    ⚠️ Account ID is required
                                  </span>
                                )}
                              </div>

                              <div style={s.field}>
                                <label style={s.label}>
                                  Access Token <span style={s.required}>*</span>
                                </label>
                                <div style={s.inputWrap}>
                                  <input
                                    style={s.input(
                                      errors["instagram.accessToken"],
                                    )}
                                    type={
                                      showPass["instagram_accessToken"]
                                        ? "text"
                                        : "password"
                                    }
                                    placeholder="Instagram or Facebook Page Access Token"
                                    value={forms.instagram?.accessToken || ""}
                                    onChange={(e) =>
                                      handleChange(
                                        "instagram",
                                        "accessToken",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    style={s.eyeIcon}
                                    onClick={() =>
                                      togglePass("instagram_accessToken")
                                    }
                                  >
                                    {showPass["instagram_accessToken"]
                                      ? "🙈"
                                      : "👁️"}
                                  </button>
                                </div>
                                {errors["instagram.accessToken"] && (
                                  <span style={s.errText}>
                                    ⚠️ Access Token is required
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {activeTab === "linkedin" && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                          }}
                        >
                          <div style={s.formSection}>
                            <div style={s.formSectionTitle}>
                              💼 LinkedIn Developer Credentials
                            </div>
                            <div style={s.grid2}>
                              <div style={s.field}>
                                <label style={s.label}>
                                  Client ID <span style={s.required}>*</span>
                                </label>
                                <input
                                  style={s.input(errors["linkedin.clientId"])}
                                  type="text"
                                  placeholder="LinkedIn Developer App Client ID"
                                  value={forms.linkedin?.clientId || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      "linkedin",
                                      "clientId",
                                      e.target.value,
                                    )
                                  }
                                />
                                {errors["linkedin.clientId"] && (
                                  <span style={s.errText}>
                                    ⚠️ Client ID is required
                                  </span>
                                )}
                              </div>

                              <div style={s.field}>
                                <label style={s.label}>
                                  Client Secret{" "}
                                  <span style={s.required}>*</span>
                                </label>
                                <div style={s.inputWrap}>
                                  <input
                                    style={s.input(
                                      errors["linkedin.clientSecret"],
                                    )}
                                    type={
                                      showPass["linkedin_clientSecret"]
                                        ? "text"
                                        : "password"
                                    }
                                    placeholder="LinkedIn Developer App Client Secret"
                                    value={forms.linkedin?.clientSecret || ""}
                                    onChange={(e) =>
                                      handleChange(
                                        "linkedin",
                                        "clientSecret",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    style={s.eyeIcon}
                                    onClick={() =>
                                      togglePass("linkedin_clientSecret")
                                    }
                                  >
                                    {showPass["linkedin_clientSecret"]
                                      ? "🙈"
                                      : "👁️"}
                                  </button>
                                </div>
                                {errors["linkedin.clientSecret"] && (
                                  <span style={s.errText}>
                                    ⚠️ Client Secret is required
                                  </span>
                                )}
                              </div>

                              <div style={s.field}>
                                <label style={s.label}>
                                  Access Token <span style={s.required}>*</span>
                                </label>
                                <div style={s.inputWrap}>
                                  <input
                                    style={s.input(
                                      errors["linkedin.accessToken"],
                                    )}
                                    type={
                                      showPass["linkedin_accessToken"]
                                        ? "text"
                                        : "password"
                                    }
                                    placeholder="OAuth Access Token"
                                    value={forms.linkedin?.accessToken || ""}
                                    onChange={(e) =>
                                      handleChange(
                                        "linkedin",
                                        "accessToken",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    style={s.eyeIcon}
                                    onClick={() =>
                                      togglePass("linkedin_accessToken")
                                    }
                                  >
                                    {showPass["linkedin_accessToken"]
                                      ? "🙈"
                                      : "👁️"}
                                  </button>
                                </div>
                                {errors["linkedin.accessToken"] && (
                                  <span style={s.errText}>
                                    ⚠️ Access Token is required
                                  </span>
                                )}
                              </div>

                              <div style={s.field}>
                                <label style={s.label}>Organization ID</label>
                                <input
                                  style={s.input()}
                                  type="text"
                                  placeholder="LinkedIn Organization numeric URN ID"
                                  value={forms.linkedin?.organizationId || ""}
                                  onChange={(e) =>
                                    handleChange(
                                      "linkedin",
                                      "organizationId",
                                      e.target.value,
                                    )
                                  }
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div style={s.actions}>
                        {(activeTab === "facebook" ||
                          activeTab === "instagram" ||
                          activeTab === "linkedin") && (
                            <button
                              type="button"
                              style={s.oauthBtn(activePlatform.color)}
                              onClick={() => handleOAuthConnect(activeTab)}
                            >
                              🔗 Connect {activePlatform.label}
                            </button>
                          )}
                        <button
                          type="button"
                          style={s.testBtn}
                          onClick={() => handleTestConnection(activeTab)}
                          disabled={testingConnection}
                        >
                          {testingConnection
                            ? "⏳ Testing..."
                            : "⚡ Test Connection"}
                        </button>
                        <button
                          type="button"
                          style={{ ...s.saveBtn, opacity: saving ? 0.7 : 1 }}
                          onClick={() => handleSave(activeTab)}
                          disabled={saving}
                        >
                          {saving ? "⏳ Saving..." : "💾 Save Configuration"}
                        </button>
                      </div>

                      {connected[activeTab] && (
                        <p
                          style={{
                            marginTop: "16px",
                            fontSize: "0.84rem",
                            color: C.success,
                            fontWeight: 600,
                          }}
                        >
                          ✅ {activePlatform.label} is connected. You can now
                          publish posts from the Enteli Social Dashboard.
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && <div style={s.toast(toast.type)}>{toast.msg}</div>}

      {/* Login Popup Modal */}
      {showLoginPopup && (
        <div style={s.modalOverlay}>
          <div style={s.modalCard}>
            <div style={s.modalHead}>
              <h3 style={s.modalTitle}>🔐 Connection Setup Required</h3>
              <button
                style={s.modalClose}
                onClick={() => setShowLoginPopup(false)}
              >
                ✕
              </button>
            </div>
            <div style={s.modalBody}>
              <div style={{ textAlign: "center", padding: "10px 0" }}>
                <span style={{ fontSize: "3.5rem" }}>🔑</span>
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: "1rem",
                  color: C.text,
                  fontWeight: 700,
                  textAlign: "center",
                }}
              >
                No Stored Credentials Found
              </p>
              <p
                style={{
                  margin: "8px 0 0 0",
                  fontSize: "0.9rem",
                  color: C.textSub,
                  lineHeight: 1.6,
                  textAlign: "center",
                }}
              >
                We couldn't retrieve configuration settings for{" "}
                <strong>
                  {PLATFORMS.find((p) => p.id === loginPopupPlatform)?.label ||
                    loginPopupPlatform}
                </strong>
                .
              </p>
              <p
                style={{
                  margin: "4px 0 0 0",
                  fontSize: "0.85rem",
                  color: C.textSub,
                  lineHeight: 1.5,
                  textAlign: "center",
                }}
              >
                Please fill in the configuration details manually or log in via
                OAuth to save your settings.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button
                type="button"
                style={{ ...s.testBtn, margin: 0 }}
                onClick={() => setShowLoginPopup(false)}
              >
                Configure Manually
              </button>
              {(loginPopupPlatform === "facebook" ||
                loginPopupPlatform === "instagram" ||
                loginPopupPlatform === "linkedin") && (
                  <button
                    type="button"
                    style={{ ...s.saveBtn, margin: 0 }}
                    onClick={() => {
                      setShowLoginPopup(false);
                      handleOAuthConnect(loginPopupPlatform);
                    }}
                  >
                    🔗 Connect via OAuth
                  </button>
                )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
